import { MouseEventHandler, PropsWithChildren } from 'react'

import { Tracking } from 'api/events/types'
import { useActiveConversationId } from 'hooks/useActiveConversationId'
import { useTriggerEvent } from 'hooks/useTriggerEvent'

import { getConfiguration } from 'services/config'
import { AppSource } from 'types/AppSource'
import { getUrlWithAppSource } from 'utils/getUrlWithAppSource'

type Props = {
  appSourceDetails?: AppSource['sourceDetails']
  appSourceType: AppSource['sourceType']
  className?: string
  onClick?: MouseEventHandler<HTMLAnchorElement>
  trackingName?: Tracking['name']
  trackingProps?: Tracking['properties']
  url: AppSource['url']
}

export const PlatformLink = ({
  appSourceDetails = {},
  appSourceType,
  children,
  className,
  onClick,
  trackingName,
  trackingProps,
  url,
}: PropsWithChildren<Props>) => {
  const { activeConversationId } = useActiveConversationId()
  const triggerEvent = useTriggerEvent()

  const { hasAppSource } = getConfiguration()

  const urlWithAppSource = getUrlWithAppSource({
    sourceDetails: {
      conversationId: activeConversationId,
      ...appSourceDetails,
    },
    sourceType: appSourceType,
    url,
  })

  const appliedUrl = hasAppSource ? urlWithAppSource : url

  const handleClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (!trackingName) return
    triggerEvent({
      name: trackingName,
      properties: trackingProps,
    })
    onClick?.(e)
  }

  if (!url) {
    return <span className={className}>{children}</span>
  }

  return (
    <a
      href={appliedUrl}
      className={className}
      target="_blank"
      tabIndex={0}
      onClick={handleClick}
      rel="noreferrer"
    >
      {children}
    </a>
  )
}
